import { logger } from '@/utils/logger';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import {
  AUDIO_ERROR_MESSAGES,
  DEFAULT_VOLUME,
  MAX_SEEK_ATTEMPTS,
} from '@/constants/audioPlayerConstants';
import { AudioPlayerState } from '@/types/audio_player_types';
import {
  getAudioUrl,
  isSeekError,
  logAudioError,
  logMetadata,
  logTrackInfo,
} from '@/utils/audioPlayerUtils';
import { addTrackToHistory, getPlayHistory } from '@/utils/playHistoryUtils';

export const useAudioPlayerStore = create<AudioPlayerState>()(
  subscribeWithSelector((set, get) => ({
    // 초기 상태
    currentTrack: null,
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    volume: DEFAULT_VOLUME,
    isMuted: false,
    audioElement: null,
    isLoading: false,
    seekingDisabled: false,
    errorMessage: null,

    // 오디오 초기화
    initAudio: () => {
      const audio = new Audio();
      audio.volume = get().volume;
      audio.preload = 'auto';
      audio.crossOrigin = 'use-credentials';

      let seekErrorCount = 0; // seeking 에러 카운트

      // Event handlers stored as references for cleanup
      const handleError = () => {
        logAudioError(audio);

        // seeking 관련 에러인 경우
        if (isSeekError(audio.error)) {
          seekErrorCount++;

          // MAX_SEEK_ATTEMPTS번 이상 실패하면 seeking 비활성화
          if (seekErrorCount >= MAX_SEEK_ATTEMPTS) {
            logger.warn('Multiple seek failures detected. Disabling seek functionality.');
            set({
              seekingDisabled: true,
              errorMessage: AUDIO_ERROR_MESSAGES.SEEK_DISABLED,
              isLoading: false,
            });

            // 처음으로 되돌리기
            audio.currentTime = 0;
            if (get().isPlaying) {
              audio.play().catch((e) => logger.error('Audio play failed', e));
            }
            return;
          }
        }

        set({ isLoading: false, isPlaying: false });
      };

      const handleLoadedMetadata = () => {
        logMetadata(audio.duration);
        get().updateDuration(audio.duration);
        set({ isLoading: false });

        // 새 트랙 로드 시 seeking 에러 카운트 리셋
        seekErrorCount = 0;
      };

      const handleEnded = () => {
        // 곡이 끝나면 다음 곡 재생 시도
        const playlist = getPlayHistory();
        const currentTrack = get().currentTrack;

        if (currentTrack) {
          const currentIndex = playlist.findIndex(track => track.id === currentTrack.id);

          // 다음 곡이 있으면 자동 재생
          if (currentIndex !== -1 && currentIndex < playlist.length - 1) {
            get().playNext();
            return;
          }
        }

        // 다음 곡이 없으면 재생 중지
        audio.currentTime = 0;
        set({ isPlaying: false, currentTime: 0 });
      };

      const handleTimeUpdate = () => {
        get().updateCurrentTime(audio.currentTime);
      };

      const handleLoadStart = () => {
        set({ isLoading: true, errorMessage: null });
      };

      const handleCanPlay = () => {
        set({ isLoading: false });
      };

      const handleWaiting = () => {
        set({ isLoading: true });
      };

      const handlePlaying = () => {
        set({ isLoading: false });
      };

      // 에러 핸들링
      audio.addEventListener('error', handleError);
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('loadstart', handleLoadStart);
      audio.addEventListener('canplay', handleCanPlay);
      audio.addEventListener('waiting', handleWaiting);
      audio.addEventListener('playing', handlePlaying);

      // Store event handlers for cleanup
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (audio as any)._eventHandlers = {
        error: handleError,
        loadedmetadata: handleLoadedMetadata,
        ended: handleEnded,
        timeupdate: handleTimeUpdate,
        loadstart: handleLoadStart,
        canplay: handleCanPlay,
        waiting: handleWaiting,
        playing: handlePlaying,
      };

      set({ audioElement: audio });
    },

    // 트랙 재생
    playTrack: (track, updateHistory = true) => {
      const { audioElement, currentTrack, togglePlay } = get();

      if (!audioElement) {
        get().initAudio();
      }

      if (currentTrack?.id === track.id) {
        togglePlay();
        return;
      }

      const audio = get().audioElement!;
      audio.pause();

      const audioUrl = getAudioUrl(track);

      logTrackInfo(track, track.isPurchased || false, audioUrl);

      audio.src = audioUrl;
      audio.currentTime = 0;
      audio.load();

      // 재생 기록에 트랙 추가 (updateHistory가 true일 때만)
      if (updateHistory) {
        addTrackToHistory(track);
      }

      set({
        currentTrack: track,
        currentTime: 0,
        isPlaying: true,
      });

      audio.play().catch(error => {
        logger.error(AUDIO_ERROR_MESSAGES.PLAY_ERROR, error);
        set({ isPlaying: false });
      });
    },

    // 재생/정지 토글
    togglePlay: () => {
      const { audioElement, isPlaying, currentTrack } = get();

      if (!audioElement || !currentTrack) return;

      if (isPlaying) {
        audioElement.pause();
        set({ isPlaying: false });
      } else {
        set({ isPlaying: true });
        audioElement.play().catch(error => {
          logger.error(AUDIO_ERROR_MESSAGES.TOGGLE_ERROR, error);
          set({ isPlaying: false });
        });
      }
    },

    // 시간 이동
    // 참고: 미리듣기 제한은 백엔드 스트리밍 API에서 처리 (구매자: 전체, 미구매자: 30초)
    seek: time => {
      const { audioElement, currentTrack } = get();

      if (!audioElement || !currentTrack) return;

      try {
        audioElement.currentTime = time;
        set({ currentTime: time });
      } catch (error) {
        logger.error(AUDIO_ERROR_MESSAGES.SEEK_ERROR, { error, time });
      }
    },

    // 볼륨 설정
    setVolume: volume => {
      const { audioElement, isMuted } = get();

      set({ volume });

      if (audioElement && !isMuted) {
        audioElement.volume = volume;
      }
    },

    // 음소거 토글
    toggleMute: () => {
      const { audioElement, isMuted, volume } = get();
      const newMuted = !isMuted;

      set({ isMuted: newMuted });

      if (audioElement) {
        audioElement.volume = newMuted ? 0 : volume;
      }
    },

    // 현재 시간 업데이트
    // 참고: 미리듣기 제한은 백엔드 스트리밍 API에서 처리 (구매자: 전체, 미구매자: 30초)
    updateCurrentTime: time => {
      set({ currentTime: time });
    },

    // 미리듣기 중지 (백엔드에서 시간 제한을 처리하므로 프론트엔드에서는 비활성화)
    stopPreview: () => {
      // No-op: 백엔드 스트리밍 API에서 미리듣기 시간 제한 처리
    },

    // 전체 시간 업데이트
    updateDuration: duration => {
      set({ duration });
    },

    // 플레이어 닫기
    closePlayer: () => {
      const { audioElement } = get();

      if (audioElement) {
        audioElement.pause();
        audioElement.currentTime = 0;
      }

      set({
        currentTrack: null,
        isPlaying: false,
        currentTime: 0,
        duration: 0,
        errorMessage: null,
      });
    },

    // 다음 곡 재생
    playNext: () => {
      const { currentTrack } = get();
      if (!currentTrack) return;

      const playlist = getPlayHistory();
      const currentIndex = playlist.findIndex(track => track.id === currentTrack.id);

      if (currentIndex === -1 || currentIndex === playlist.length - 1) {
        // 마지막 곡이거나 플레이리스트에 없으면 재생 중지
        logger.info('No next track available');
        return;
      }

      const nextTrack = playlist[currentIndex + 1];
      get().playTrack(nextTrack, false); // updateHistory를 false로 설정 (순서 유지)
    },

    // 이전 곡 재생
    playPrevious: () => {
      const { currentTrack } = get();
      if (!currentTrack) return;

      const playlist = getPlayHistory();
      const currentIndex = playlist.findIndex(track => track.id === currentTrack.id);

      if (currentIndex === -1 || currentIndex === 0) {
        // 첫 번째 곡이거나 플레이리스트에 없으면 재생 중지
        logger.info('No previous track available');
        return;
      }

      const previousTrack = playlist[currentIndex - 1];
      get().playTrack(previousTrack, false); // updateHistory를 false로 설정 (순서 유지)
    },

    // 에러 메시지 제거
    clearError: () => {
      set({ errorMessage: null });
    },

    // 정리
    cleanup: () => {
      const { audioElement } = get();

      if (audioElement) {
        audioElement.pause();

        // Remove all event listeners to prevent memory leaks
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const handlers = (audioElement as any)._eventHandlers;
        if (handlers) {
          audioElement.removeEventListener('error', handlers.error);
          audioElement.removeEventListener('loadedmetadata', handlers.loadedmetadata);
          audioElement.removeEventListener('ended', handlers.ended);
          audioElement.removeEventListener('timeupdate', handlers.timeupdate);
          audioElement.removeEventListener('loadstart', handlers.loadstart);
          audioElement.removeEventListener('canplay', handlers.canplay);
          audioElement.removeEventListener('waiting', handlers.waiting);
          audioElement.removeEventListener('playing', handlers.playing);
        }

        audioElement.src = '';
      }

      set({
        currentTrack: null,
        isPlaying: false,
        currentTime: 0,
        duration: 0,
        audioElement: null,
        isLoading: false,
        seekingDisabled: false,
        errorMessage: null,
      });
    },
  })),
);

// 초기화 함수
export const initializeAudioPlayer = () => {
  useAudioPlayerStore.getState().initAudio();
};

// 정리 함수
export const cleanupAudioPlayer = () => {
  useAudioPlayerStore.getState().cleanup();
};
